"use client";

import React, { useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

import { useLocale } from "@/lib/useLocale";
import { useDictionary } from "@/lib/useDictionary";

import Navbar from "./Navbar";
import Footer from "./Footer";
import FloatingActionButton from "./FloatingActionButton";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export default function NotFoundContent() {
  const locale = useLocale();
  const dict = useDictionary();
  const pathname = usePathname();
  const page = dict.notFoundPage;

  // 把 404 送進 GTM，之後能在 GA4 看出哪些壞連結最常被踩到。
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "page_not_found",
      page_path: pathname,
      referrer: document.referrer,
    });
  }, [pathname]);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <section className="flex min-h-[70vh] items-center py-20 gradient-blue">
          <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <p className="bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-700 bg-clip-text text-7xl font-black text-transparent drop-shadow-[0_10px_30px_rgba(37,99,235,0.18)] sm:text-8xl">
                {page.code}
              </p>
              <h1 className="mt-4 text-3xl font-bold text-[var(--blue-900)] sm:text-4xl">
                {page.heading}
              </h1>
            </motion.div>

            <div className="mt-12 grid gap-6 sm:grid-cols-2">
              {page.links.map((link, index) => {
                const href = `/${locale}${link.href === "/" ? "" : link.href}`;

                return (
                  <motion.div
                    key={link.href}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                  >
                    <Link
                      href={href}
                      className="group flex h-full items-start justify-between gap-4 rounded-2xl border border-gray-100 bg-white p-6 shadow-lg transition-all duration-300 hover:shadow-xl"
                    >
                      <span>
                        <span className="block font-semibold text-[var(--blue-900)]">
                          {link.label}
                        </span>
                        <span className="mt-1 block text-sm text-gray-600">
                          {link.description}
                        </span>
                      </span>
                      <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-[var(--blue-500)] transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingActionButton />
    </>
  );
}
